import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	LIBRARY_FILE_ACCEPT,
	LIBRARY_FILE_MAX_BYTES,
	useUploadLibraryFilesMutation,
} from '../../../queries/Attachments/useUploadLibraryFilesMutation';
import { CloudUploadIcon } from '../../components/svgs/icons/CloudUploadIcon';
import { Button } from '../Button';
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from '../Dialog';
import { fileStyles } from './styles';

/**
 * Batches stay under the endpoint's 20MB request cap, with headroom for multipart boundaries, so a bulk
 * selection uploads in several requests instead of failing whole with an opaque 413.
 */
const MAX_UPLOAD_BATCH_BYTES = 15_000_000;

const ALLOWED_EXTENSIONS = LIBRARY_FILE_ACCEPT.split(',');

interface AddFilesDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	impersonationContext?: Api.IImpersonationContext;
}

function batchBySize(files: File[]) {
	const batches: File[][] = [];
	let current: File[] = [];
	let currentBytes = 0;

	for (const file of files) {
		// A file larger than the budget still goes on its own rather than being dropped; the per-file cap
		// keeps it under the request limit anyway.
		if (current.length && currentBytes + file.size > MAX_UPLOAD_BATCH_BYTES) {
			batches.push(current);
			current = [];
			currentBytes = 0;
		}
		current.push(file);
		currentBytes += file.size;
	}

	if (current.length) {
		batches.push(current);
	}

	return batches;
}

function hasAllowedExtension(file: File) {
	return ALLOWED_EXTENSIONS.some(extension => file.name.toLowerCase().endsWith(extension));
}

export function AddFilesDialog({ open, onOpenChange, impersonationContext }: AddFilesDialogProps) {
	const [dragActive, setDragActive] = React.useState(false);
	const [rejected, setRejected] = React.useState<string[]>([]);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const uploadMutation = useUploadLibraryFilesMutation({
		onError: () => toast.error('Could not upload those files. Try again.'),
	});

	const upload = async (chosen: File[]) => {
		const tooBig = chosen.filter(file => file.size > LIBRARY_FILE_MAX_BYTES);
		const wrongType = chosen.filter(file => !hasAllowedExtension(file));
		const accepted = chosen.filter(file => file.size <= LIBRARY_FILE_MAX_BYTES && hasAllowedExtension(file));

		setRejected([
			...wrongType.map(file => `${file.name} is not a supported file type.`),
			...tooBig.map(file => `${file.name} is larger than 20MB.`),
		]);

		if (!accepted.length) {
			return;
		}

		const results = await Promise.all(
			batchBySize(accepted).map(batch => uploadMutation.mutateAsync({ files: batch, impersonationContext }))
		);

		const added = results.reduce((total, result) => total + (result.succeeded?.length ?? 0), 0);
		const failed = results.reduce((total, result) => total + (result.failed?.length ?? 0), 0);

		if (failed) {
			toast.error(added ? `Added ${added}. ${failed} could not be added.` : 'Could not add those files.');
			return;
		}

		if (added) {
			toast.success(added === 1 ? 'File added' : `Added ${added} files`);
			onOpenChange(false);
		}
	};

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(e.type === 'dragenter' || e.type === 'dragover');
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files?.length) {
			upload([...e.dataTransfer.files]);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.length) {
			upload([...e.target.files]);
		}
		// Lets the same file be picked twice in a row, which otherwise fires no change event.
		e.target.value = '';
	};

	const openPicker = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		fileInputRef.current?.click();
	};

	return (
		<Dialog
			onOpenChange={next => {
				if (!next) {
					setRejected([]);
				}
				onOpenChange(next);
			}}
			open={open}
		>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Add files</DialogTitle>
				</DialogHeader>
				<div className={css(fileStyles.dropzoneWrapper)}>
					<div
						className={css(fileStyles.dropzone)}
						data-drag-active={dragActive || undefined}
						onClick={openPicker}
						onDragEnter={handleDrag}
						onDragLeave={handleDrag}
						onDragOver={handleDrag}
						onDrop={handleDrop}
					>
						<div className={css(fileStyles.dropzoneContent)}>
							<CloudUploadIcon className={css(fileStyles.dropzoneIcon)} />
							<h3 className={css(fileStyles.dropzoneTitle)}>
								{uploadMutation.isPending ? 'Uploading...' : 'Drop files here or click to browse'}
							</h3>
							<p className={css(fileStyles.dropzoneDescription)}>
								Reference files for your Levitate team, like a brand guide. Supports PDF, Word, PowerPoint, and text
								files up to 20MB.
							</p>
							{uploadMutation.isPending ? null : (
								<Button onClick={openPicker} type='button' variant='primary'>
									Choose Files
								</Button>
							)}
						</div>
						<input
							accept={LIBRARY_FILE_ACCEPT}
							className={css(fileStyles.fileInput)}
							data-testid='image-library-file-input'
							multiple
							onChange={handleChange}
							ref={fileInputRef}
							type='file'
						/>
					</div>
					{rejected.length ? (
						<div className={css(fileStyles.rejectedFiles)}>
							{rejected.map(message => (
								<div key={message}>{message}</div>
							))}
						</div>
					) : null}
				</div>
			</DialogPopup>
		</Dialog>
	);
}
